import StepHandlerFactory from './StepHandlerFactory';
import { STEPS } from '../constants/steps';

/**
 * Controller for managing step handlers and their interactions
 */
export default class StepController {
    /**
     * Create a new StepController
     * 
     * @param {object} canvas - STCCanvas instance
     * @param {object} stepManager - StepManager instance
     */
    constructor(canvas, stepManager) {
        this.canvas = canvas;
        this.stepManager = stepManager;
        this.activeStepId = null;
        this.handlers = {
            front: new Map(), // Map of stepId => handler instance for front side
            back: new Map()   // Map of stepId => handler instance for back side
        };
        
        this.bindEvents();
    }

    /**
     * Initialize handlers for both sides at once
     */
    initializeAllHandlers() {
        // Create front handlers
        STEPS.front.forEach(stepDef => {

            if( this.handlers.front.has(stepDef.id) ) return;

            let elementExists = this.canvas.getElement(stepDef.elements?.[0]) != undefined;
            if(!elementExists && stepDef.optional) return;

            const handler = this.createHandler(stepDef.id, 'front');
            this.handlers.front.set(stepDef.id, handler);
            handler.initialize();
            this.stepManager.updateCompletedStatusForStep(stepDef.id);

        });
        
        // Create back handlers
        STEPS.back.forEach(stepDef => {

            if( this.handlers.back.has(stepDef.id) ) return;

            let elementExists = this.canvas.getElement(stepDef.elements?.[0]) != undefined;
            if(!elementExists && stepDef.optional) return;

            const handler = this.createHandler(stepDef.id, 'back');
            this.handlers.back.set(stepDef.id, handler);
            handler.initialize();
            this.stepManager.updateCompletedStatusForStep(stepDef.id);

        });
        
        // Initialize handlers for current side
        this.activateHandlersForSide(this.stepManager.currentSide);
    }

    /**
     * Activate handlers for the current side (call processTemplate on each)
     * @param {string} side - 'front' or 'back'
     */
    activateHandlersForSide(side) {
        // Process each handler's template
        this.handlers[side].forEach(handler => {
            handler.processTemplate();
        });
        
        // Notify StepManager that handlers are ready
        this.stepManager.notifyHandlersReady();
    }

    /**
     * Update handler visibility for all handlers on side change
     * @param {string} side - 'front' or 'back'
     */
    updateHandlerVisibility(side) {
        const previousSide = side === 'front' ? 'back' : 'front';
        
        // Hide previous side handlers
        this.handlers[previousSide].forEach(handler => {
            handler.handleSideChange(false);
        });
        
        // Show current side handlers
        this.handlers[side].forEach(handler => {
            handler.handleSideChange(true);
        });
    }
    
    /**
     * Bind to step manager events
     */
    bindEvents() {
        // Listen for step changes
        this.stepManager.on('stepChange', ({ detail }) => {
            this.handleStepChange(detail.stepId);
        });

        // Listen for step data changes
        this.stepManager.on('stepDataChange', ({ detail }) => {
            this.handleStepDataChange(detail.stepId, detail.data);
        });
        
        // Listen for side changes
        this.stepManager.on('sideChange', ({ detail }) => {
            this.handleSideChange(detail.side);
        });

        this.canvas.on('templateReady', ({ detail }) => {
            this.applyHandlersToTemplate(detail.side);
        });
    }
    
    /**
     * Create a handler for a specific step
     * 
     * @param {string} stepId - Step ID
     * @param {string} side - 'front' or 'back'
     * @returns {object} The created handler
    */
    createHandler(stepId, side) {
        // Find step definition
        const stepDef = STEPS[side].find(step => step.id === stepId);
        if (!stepDef) return null;
        
        // Create handler instance with side information
        const handler = StepHandlerFactory.createHandler(
            stepId, 
            stepDef, 
            this.canvas, 
            this.stepManager,
            side // Pass the side to the handler
        );
        
        return handler;
    }
    
    /**
     * Initialize handlers for all steps on a side
     * 
     * @param {string} side - 'front' or 'back'
     */
    initializeHandlersForSide(side) {
        // Check if handlers for this side already exist
        if (this.handlers[side].size === 0) {
            // If not, initialize all handlers
            this.initializeAllHandlers();
        } else {
            // Just activate the existing handlers
            this.activateHandlersForSide(side);
        }
    }
    
    /**
     * Check if we have a handler for a specific step on the current side
     * 
     * @param {string} stepId - Step ID
     * @returns {boolean} True if handler exists
     */
    hasHandler(stepId) {
        return this.handlers[this.stepManager.currentSide].has(stepId);
    }
    
    /**
     * Get a handler by step ID on the current side
     * 
     * @param {string} stepId - Step ID
     * @returns {object} The handler
     */
    getHandler(stepId) {
        return this.handlers[this.stepManager.currentSide].get(stepId);
    }

    /**
     * Get a handler by step ID for a specific side
     * Useful for export operations
     * 
     * @param {string} side - 'front' or 'back'
     * @param {string} stepId - Step ID
     * @returns {object} The handler or null if not found
     */
    getHandlerForSide(side, stepId) {
        return this.handlers[side].get(stepId);
    }

    /**
     * Get all active handlers for a side
     * 
     * @param {string} side - 'front' or 'back'
     * @returns {Map} Map of stepId => handler
     */
    getAllHandlersForSide(side) {
        return this.handlers[side];
    }
    
    /**
     * Create a panel for a specific step
     * 
     * @param {string} stepId - Step ID
     * @param {HTMLElement} container - Container element
     * @returns {HTMLElement} The created panel
     */
    createPanelForStep(stepId, container) {
        const handler = this.getHandler(stepId);
        if (handler) {
            return handler.createPanel(container);
        }
        return null;
    }
    
    /**
     * Handle step activation
     * 
     * @param {string} stepId - Step ID
     */
    handleStepChange(stepId) {
        // Deactivate previous step
        if (this.activeStepId) {
            const prevHandler = this.getHandler(this.activeStepId);
            if (prevHandler) {
                prevHandler.deactivate();
            }
        }

        // Activate new step
        this.activeStepId = stepId;
        if (stepId) {
            const handler = this.getHandler(stepId);
            if (handler) {
                handler.activate();
                
                // Apply current step data
                const stepData = this.stepManager.getStepData(stepId);
                if (stepData) {
                    handler.update(stepData);
                }
            }
        }
    }
    
    /**
     * Handle step data changes
     * 
     * @param {string} stepId - Step ID
     * @param {object} data - Step data
     */
    handleStepDataChange(stepId, data) {
        const handler = this.getHandler(stepId);
        if (handler) {
            handler.update(data);
        }
    }
    
    /**
     * Handle side changes
     * 
     * @param {string} side - 'front' or 'back'
     */
    handleSideChange(side) {
        // Deactivate previous active step
        if (this.activeStepId) {
            const prevHandler = this.getHandler(this.activeStepId);
            if (prevHandler) prevHandler.deactivate();
        }
        
        // Reset active step
        this.activeStepId = null;
        
        // Update handler visibility
        this.updateHandlerVisibility(side);
        
        // Apply handlers to template elements
        this.applyHandlersToTemplate(side);
    }

    applyHandlersToTemplate(side) {
        // Check if we have handlers for this side
        if (this.handlers[side].size === 0) {
            // If not, initialize all handlers
            this.initializeAllHandlers();
            return;
        }
        
        // Process template for each handler
        this.handlers[side].forEach(handler => {
            handler.processTemplate();
            
            // Apply any existing step data
            const stepData = this.stepManager.getStepDataForSide(side, handler.stepId);
            if (stepData) {
                handler.update(stepData);
            }
            this.stepManager.updateCompletedStatusForStep(handler.stepId);
        });
        
        // Notify step manager that handlers are ready
        this.stepManager.notifyHandlersReady();
    }
    
    /**
     * Clean up handlers when no longer needed
     * 
     * @param {string} side - Optional side to dispose, if not provided, disposes all
     */
    disposeHandlers(side = null) {
        if (side) {
            // Dispose handlers for the specified side
            this.handlers[side].forEach(handler => {
                if (typeof handler.hide === 'function') {
                    handler.hide();
                }
                handler.dispose();
            });
            this.handlers[side].clear();
        } else {
            // Dispose all handlers
            for (const side in this.handlers) {
                this.handlers[side].forEach(handler => {
                    if (typeof handler.hide === 'function') {
                        handler.hide();
                    }
                    handler.dispose();
                });
                this.handlers[side].clear();
            }
        }
    }

    /**
     * Get all handlers for both sides
     * This is useful for export operations
     * 
     * @returns {object} All handlers organized by side
     */
    getAllHandlers() {
        return {
            front: Array.from(this.handlers.front.entries()).map(([stepId, handler]) => ({ stepId, handler })),
            back: Array.from(this.handlers.back.entries()).map(([stepId, handler]) => ({ stepId, handler }))
        };
    }

    /**
     * Export data from all handlers
     * Collects export data from all active handlers on both sides
     *
     * @returns {object} Exported data organized by side and step
     */
    exportAllHandlerData() {
        const exportData = {
            front: {},
            back: {}
        };

        // Export front side data
        this.handlers.front.forEach((handler, stepId) => {
            if (this.stepManager.completedSteps.front.has(stepId)) {
                exportData.front[stepId] = handler.getStepExportData();
            }
        });

        // Export back side data
        this.handlers.back.forEach((handler, stepId) => {
            if (this.stepManager.completedSteps.back.has(stepId)) {
                exportData.back[stepId] = handler.getStepExportData();
            }
        });

        return exportData;
    }

    /**
     * Export data from all handlers in template format
     * Collects data from completed handlers and formats it like template.json
     *
     * @returns {object} Exported data in template format with elements structure
     */
    exportAllHandlerDataAsTemplate() {
        const exportData = {
            front: {
                elements: {}
            },
            back: {
                elements: {}
            }
        };

        // Export front side data in template format
        this.handlers.front.forEach((handler, stepId) => {
            if (this.stepManager.completedSteps.front.has(stepId)) {
                this.exportHandlerAsTemplateElements(handler, exportData.front.elements);
            }
        });

        // Export back side data in template format
        this.handlers.back.forEach((handler, stepId) => {
            if (this.stepManager.completedSteps.back.has(stepId)) {
                this.exportHandlerAsTemplateElements(handler, exportData.back.elements);
            }
        });

        return exportData;
    }

    /**
     * Export a single handler's elements in template format
     *
     * @param {BaseStepHandler} handler - The handler to export
     * @param {object} elementsContainer - Container to store template elements
     */
    exportHandlerAsTemplateElements(handler, elementsContainer) {
        // Get element IDs that this handler manages
        const elementIds = handler.definition.elements || [];

        elementIds.forEach(elementId => {
            // Get template-formatted data for this element
            const templateElementData = handler.getTemplateElementData(elementId);

            if (templateElementData) {
                // Store using element ID as key (not step ID)
                elementsContainer[elementId] = templateElementData;
            }
        });
    }
}